import { c as _c } from "react/compiler-runtime";
import { ForecastDrawn, QuestionBounds, ForecastQuestionCfgAmount, Fmt } from 'shared/forecast';
import { Bounds } from 'shared/forecast/bounds';
import { entries, concat } from 'shared/collection';
import { Rnge } from 'shared/range';
import { fmtNum } from 'shared/format';
import { StyleProps } from 'shared/ui/uiType';
import { notNull } from 'shared/type';
import { DeltaValuesDynamic } from 'shared/paths';
import { useMemoValue, useElementSize } from 'shared/ui';
import { Dist, DistDrawn, DistributionChart, DistributionChartProps } from 'shared/chart';
import { ResultColorFn } from 'shared/forecast/results/QuestionResults';
import { ActionIcon, Center, SegmentedControl, Tooltip, useSafeMantineTheme } from '@mantine/core';
import React, { ComponentProps, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clamp, flat, indexBy, keys, last, mapToObj, mapValues, sortBy, unique } from 'remeda';
import { ScaleLinear, scaleLinear } from 'd3-scale';
import { useMergedRef, useHotkeys } from '@mantine/hooks';
import { OnDragProps, useClickSansDrag, useDrag, useLatest, useMouse } from 'components/hooks';
import { HiHand, HiLockOpen, HiPlusCircle, HiTrash, HiX } from 'react-icons/hi';
import { GrSelect } from 'react-icons/gr';
import { IconContext } from 'react-icons';
import { KbdComboLabel, KbdComboTip, useKeyPress } from 'components/Keyboard';
import { HiLockClosed } from 'react-icons/hi2';
import { solveForV } from 'shared/solver';
import { nanoid } from 'nanoid';
import { useKbd } from 'components/Keyboard';
import { ForecastAmountTailEdit, TailEditProps, useHardBoundTails } from './ForecastAmountTail';
import { match } from 'ts-pattern';
import { P } from 'ts-pattern';
import { twMerge } from 'tailwind-merge';
const {
  round
} = Math;
const {
  debug: consoleDebug
} = console;
type ForecastDrawnEditProps = {
  forecast: ForecastDrawn;
  dist: Dist.Distribution;
  question: Pick<ForecastQuestionCfgAmount, 'defaultBounds' | 'forecastType' | 'unitLabel' | 'numberFormat' | 'title' | 'amountType' | 'hardBounds'>;
  onDelta: (value: DeltaValuesDynamic) => void;
  color: ResultColorFn;
  showGroup?: boolean;
  margin?: DistributionChartProps['margin'];
} & StyleProps;
export const ForecastDrawnEdit = t0 => {
  const $ = _c(47);
  const {
    forecast,
    dist,
    question,
    onDelta,
    showGroup,
    margin,
    className,
    style
  } = t0;
  let t1;
  if ($[0] !== forecast || $[1] !== onDelta || $[2] !== question) {
    t1 = {
      forecast,
      question,
      onDelta
    };
    $[0] = forecast;
    $[1] = onDelta;
    $[2] = question;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const uPoints = usePoints(t1);
  const kbd = useKbd();
  const t2 = `flex flex-col h-full ${className ?? ""}`;
  let t3;
  if ($[4] !== style) {
    t3 = {
      style
    };
    $[4] = style;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      size: "16px"
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = uPoints.mode;
  let t6;
  if ($[7] !== uPoints) {
    t6 = v => uPoints.setMode(v as Mode);
    $[7] = uPoints;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      value: "select",
      label: <Tooltip withinPortal={true} label="Selection mode"><div><GrSelect /></div></Tooltip>
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== kbd) {
    t8 = kbd.mapCombo({
      key: "Meta"
    });
    $[10] = kbd;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <KbdComboTip title="Add/remove points mode" combo={t8} />;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div><HiPlusCircle /></div>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t9) {
    t11 = {
      value: "edit",
      label: <Tooltip withinPortal={true} label={t9}>{t10}</Tooltip>
    };
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== kbd) {
    t12 = kbd.mapCombo({
      key: " "
    });
    $[17] = kbd;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12) {
    t13 = <KbdComboTip title="Pan mode" combo={t12} />;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div><HiHand /></div>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t13) {
    t15 = {
      value: "drag",
      label: <Tooltip withinPortal={true} label={t13}>{t14}</Tooltip>
    };
    $[22] = t13;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t11 || $[25] !== t15) {
    t16 = [t7, t11, t15];
    $[24] = t11;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16 || $[28] !== t6 || $[29] !== uPoints.mode) {
    t17 = <SegmentedControl value={t5} onChange={t6} data={t16} />;
    $[27] = t16;
    $[28] = t6;
    $[29] = uPoints.mode;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== uPoints) {
    t18 = uPoints.selectedIds.length > 0 && <ActionIcon><HiTrash onClick={() => uPoints.deletePoints(uPoints.selectedIds)} /></ActionIcon>;
    $[31] = uPoints;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <div className="flex h-8 items-center gap-2"><IconContext.Provider value={t4}>{t17}{t18}</IconContext.Provider></div>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== dist || $[37] !== margin || $[38] !== question || $[39] !== showGroup || $[40] !== uPoints) {
    t20 = <DrawCanvas className="grow" {...{
      uPoints,
      showGroup,
      question,
      dist,
      margin
    }} />;
    $[36] = dist;
    $[37] = margin;
    $[38] = question;
    $[39] = showGroup;
    $[40] = uPoints;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t19 || $[43] !== t2 || $[44] !== t20 || $[45] !== t3) {
    t21 = <div className={t2} {...t3}>{t19}{t20}</div>;
    $[42] = t19;
    $[43] = t2;
    $[44] = t20;
    $[45] = t3;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
type Mode = 'select' | 'edit' | 'drag';
type PointUI = {
  selected?: boolean;
};
type PointSaved = ForecastDrawn['points'][number];
const usePoints = t0 => {
  const $ = _c(45);
  const {
    forecast,
    question,
    onDelta
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [ptUIState, setPtState] = useState(t1);
  const [mode, setMode] = useState("select");
  const shift = useKeyPress("Shift");
  const ctrl = useKeyPress("Meta");
  const [previousMode, setPreviousMode] = useState("select");
  let t2;
  if ($[1] !== ctrl || $[2] !== mode || $[3] !== previousMode || $[4] !== shift) {
    t2 = () => {
      if (ctrl) {
        setPreviousMode(mode);
        setMode("edit");
      } else {
        if (shift) {
          setPreviousMode(mode);
          setMode("drag");
        } else {
          setMode(previousMode);
        }
      }
    };
    $[1] = ctrl;
    $[2] = mode;
    $[3] = previousMode;
    $[4] = shift;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== ctrl || $[7] !== shift) {
    t3 = [ctrl, shift];
    $[6] = ctrl;
    $[7] = shift;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== ptUIState) {
    t4 = t5 => {
      const {
        delta,
        others,
        debugName,
        ...props
      } = t5;
      const ids = concat(props.id, props.ids);
      const newState = mapToObj(unique(concat(ids, keys(ptUIState))), k => {
        const existing = ptUIState[k];
        return [k, {
          ...existing,
          ...(ids.includes(k) ? delta ?? {} : others ?? {})
        }];
      });
      setPtState(newState);
    };
    $[9] = ptUIState;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const setUIState = t4;
  let deletePoints;
  let newPoint;
  let t5;
  if ($[11] !== forecast.points || $[12] !== onDelta || $[13] !== ptUIState || $[14] !== setUIState) {
    const points = forecast?.points ?? [];
    newPoint = pt => {
      onDelta({
        points: sortBy([...points, pt], _temp)
      });
      setUIState({
        id: pt.id,
        delta: {
          selected: true
        },
        others: {
          selected: false
        }
      });
    };
    deletePoints = ids_0 => onDelta({
      points: points.filter(p_0 => !ids_0.includes(p_0.id))
    });
    t5 = points?.filter(p_1 => ptUIState[p_1.id]?.selected).map(_temp2) ?? [];
    $[11] = forecast.points;
    $[12] = onDelta;
    $[13] = ptUIState;
    $[14] = setUIState;
    $[15] = deletePoints;
    $[16] = newPoint;
    $[17] = t5;
  } else {
    deletePoints = $[15];
    newPoint = $[16];
    t5 = $[17];
  }
  const selectedIds = t5;
  let t6;
  if ($[18] !== forecast.bounds || $[19] !== question.defaultBounds) {
    t6 = Bounds.withDefaults(forecast.bounds, question.defaultBounds);
    $[18] = forecast.bounds;
    $[19] = question.defaultBounds;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const {
    bounds,
    boundsTuple
  } = t6;
  let t7;
  if ($[21] !== forecast.hardBounds || $[22] !== question.hardBounds) {
    t7 = {
      ...question.hardBounds,
      ...forecast.hardBounds
    };
    $[21] = forecast.hardBounds;
    $[22] = question.hardBounds;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const hardBounds = t7;
  let t8;
  if ($[24] !== deletePoints || $[25] !== selectedIds) {
    t8 = ["Delete", () => deletePoints(selectedIds)];
    $[24] = deletePoints;
    $[25] = selectedIds;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== deletePoints || $[28] !== selectedIds) {
    t9 = ["Backspace", () => deletePoints(selectedIds)];
    $[27] = deletePoints;
    $[28] = selectedIds;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t8 || $[31] !== t9) {
    t10 = [t8, t9];
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  useHotkeys(t10);
  let t11;
  if ($[33] !== bounds || $[34] !== boundsTuple || $[35] !== deletePoints || $[36] !== forecast || $[37] !== hardBounds || $[38] !== mode || $[39] !== newPoint || $[40] !== onDelta || $[41] !== ptUIState || $[42] !== selectedIds || $[43] !== setUIState) {
    t11 = {
      ptUIState,
      setUIState,
      newPoint,
      deletePoints,
      selectedIds,
      bounds,
      hardBounds,
      boundsTuple,
      forecast,
      onDelta,
      mode,
      setMode
    };
    $[33] = bounds;
    $[34] = boundsTuple;
    $[35] = deletePoints;
    $[36] = forecast;
    $[37] = hardBounds;
    $[38] = mode;
    $[39] = newPoint;
    $[40] = onDelta;
    $[41] = ptUIState;
    $[42] = selectedIds;
    $[43] = setUIState;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
};
type UsePoints = ReturnType<typeof usePoints>;
export const makePoint = (pt: {
  x: number;
  p: number;
}): PointSaved => ({
  x: pt.x,
  p: pt.p,
  id: nanoid(7)
});
const percentileStats = (dist: Dist.DiscreteDistribution, xRange: [number, number]) => {
  const startPercentile = dist.adjustedPt(xRange[0]).pAcc * 100;
  const endPercentile = dist.adjustedPt(xRange[1]).pAcc * 100;
  const percentiles = endPercentile - startPercentile;
  return {
    percentiles,
    startPercentile,
    endPercentile
  };
};
type Stats = ReturnType<typeof percentileStats>;

/** we want padding in px around the question bound if it isn't a hard bound*/
const boundsPadPx = 10;
const DrawCanvas = ({
  uPoints,
  style,
  className,
  ...props
}: {
  uPoints: UsePoints;
} & Pick<ForecastDrawnEditProps, 'dist' | 'showGroup' | 'question' | 'margin'> & StyleProps) => {
  const {
    colors
  } = useSafeMantineTheme();
  const {
    ref: sizeRef,
    height,
    width
  } = useElementSize();
  const {
    question,
    dist,
    showGroup,
    margin
  } = props;
  const {
    forecast,
    ptUIState,
    setUIState,
    boundsTuple,
    bounds,
    hardBounds,
    mode,
    onDelta
  } = uPoints;
  const editMode = mode == 'edit';
  const m = useMemoValue({
    left: margin?.left ?? 16,
    right: margin?.right ?? 16,
    bottom: 30,
    top: 40
  });
  const [hovered, setHovered] = useState<string | undefined>();
  const {
    scale: scale_0
  } = useMemo(() => {
    if (!width) return {};
    const xRange = [m.left, width - m.right];
    const xRatioInitial = Rnge.width(boundsTuple) / Rnge.width(xRange);
    const boundsPad = boundsPadPx * xRatioInitial;
    const boundsWithPad = [boundsTuple[0] - boundsPad, boundsTuple[1] + boundsPad]; // we want to see a little outside of the bounds
    const x = scaleLinear(boundsWithPad, xRange);
    const y = scaleLinear([0, 1], [height - m.bottom, m.top]);
    const adjustDelta = (s: ScaleLinear<number, number>) => Rnge.width(s.domain()) / Rnge.width(s.range());
    const scale = {
      x,
      y,
      xRatio: adjustDelta(x),
      yRatio: adjustDelta(y)
    };
    return {
      scale
    };
  }, [boundsTuple.join('|'), width, height, m]);
  const [tailEditing, setTailEditing] = useState({
    left: false,
    right: false
  });
  const tailProps = (side: 'left' | 'right'): TailEditProps | undefined => {
    if (!scale_0) return undefined;
    const point = side == 'left' ? forecast.points[0] : last(forecast.points);
    //  don't show tail config if at 0, or inside the question bounds
    const canHaveTails = point?.x != null && point.p > 0 && question.defaultBounds && !Rnge.between(point.x, Bounds.toTuple(question.defaultBounds));
    if (!canHaveTails) return undefined;
    return {
      type: 'tail',
      side,
      pt: point,
      tails: forecast.tails,
      onChange: v => onDelta({
        [`tails.${side}`]: v
      }),
      opened: tailEditing[side],
      setOpened: v_0 => setTailEditing({
        ...tailEditing,
        [side]: v_0
      }),
      xScale: scale_0.x,
      yScale: scale_0.y
    };
  };
  const tails = [tailProps('left'), tailProps('right')].filter(notNull);
  const tailsHard = useHardBoundTails({
    forecast,
    question,
    onDelta
  });
  const mouse = useMouse();
  useEffect(mouse.reset, [editMode]);
  const [xSelect, setXSelect] = useState<{
    locked: boolean;
    range: [number, number];
  }>();
  const clampX = (v_1: number) => clamp(v_1, {
    min: scale_0?.x.domain()[0],
    max: scale_0?.x.domain()[1]
  });
  const clampP = (v_2: number) => clamp(v_2, {
    min: 0,
    max: 1
  });
  const latest = useLatest({
    ...props,
    ...uPoints,
    scale: scale_0,
    hovered,
    mouse,
    xSelect,
    setXSelect,
    editMode,
    clampX,
    clampP,
    hardBounds
  }); // things the drag callbacks need to read without getting new scopes

  const pointClicked = useRef<{
    id: string;
    ts: number;
  } | undefined>(undefined);
  const dragState = useRef<{
    dragPoints?: {
      points: Record<string, PointSaved>;
      stats?: Stats;
    };
    startX?: number;
    startBounds?: QuestionBounds;
  }>(null);
  const canvasDrag = useDrag({
    normalize: false,
    shouldClamp: true,
    disabled: tailEditing.left || tailEditing.right,
    onChange: useCallback(({
      delta
    }: OnDragProps) => {
      if (!latest.current) return;
      const {
        forecast: forecast_0,
        ptUIState: ptUIState_0,
        boundsTuple: boundsTuple_0,
        hardBounds: hardBounds_0,
        onDelta: onDelta_0,
        scale: scale_1,
        xSelect: xSelect_0,
        setXSelect: setXSelect_0,
        clampX: clampX_0,
        mode: mode_0
      } = latest.current;
      if (!forecast_0 || !scale_1) return;
      match(mode_0).with('drag', () => {
        const dx = scale_1.xRatio * delta.x;
        const {
          min,
          max
        } = dragState.current?.startBounds ?? uPoints.bounds;
        onDelta_0({
          bounds: {
            min: min - dx,
            max: max - dx
          }
        });
      }).otherwise(() => {
        if (!dragState.current) return;
        const {
          dragPoints,
          startX
        } = dragState.current;
        if (dragPoints) {
          const selected = new Set(entries(ptUIState_0).filter(([_, p]) => p.selected).map(([id]) => id));

          // dragged points
          const draggedPoints = forecast_0.points.filter(p_0 => selected.has(p_0.id) && p_0.id in dragPoints.points).map(p_1 => {
            const newX = dragPoints.points[p_1.id]!.x + scale_1.xRatio * delta.x;
            const newP = clampP(dragPoints.points[p_1.id]!.p - scale_1.yRatio * delta.y);

            // Check if point is being dragged outside bounds
            const dxPan = match(newX).with(P.number.lt(bounds.min), x_0 => x_0 - bounds.min).with(P.number.gt(bounds.max), x_1 => x_1 - bounds.max).otherwise(() => undefined);
            if (dxPan) {
              // Expand bounds in the direction of drag
              onDelta_0({
                bounds: {
                  min: dxPan < 0 ? bounds.min + dxPan : bounds.min,
                  max: dxPan > 0 ? bounds.max + dxPan : bounds.max
                }
              });
              return {
                ...p_1,
                x: newX,
                p: newP
              };
            }
            return {
              ...p_1,
              x: clampX_0(newX),
              p: newP
            };
          });
          const withAdjustPoints = (...adjustments: PointSaved[][]) => forecast_0.points.map(p_2 => flat(adjustments).find(dp => dp.id == p_2.id) ?? p_2);
          let compensatingAdjustments = [] as PointSaved[];
          if (xSelect_0 && xSelect_0.locked && dragPoints.stats) {
            const getNewDist = (adjustments_0: PointSaved[]) => {
              const pdf = DistDrawn.cfgToPdf({
                points: withAdjustPoints(draggedPoints, adjustments_0),
                hardBounds: hardBounds_0,
                tails: forecast_0.tails
              });
              // we want to quickly adjust the other point so make the sample small
              return Dist.pdfToDiscrete(pdf, {
                range: Rnge.create(pdf.bounds, 100).map(x_2 => ({
                  x: x_2
                }))
              });
            };
            // const statsBefore = percentileStats(dist!, xSelect!.range)
            const isEndingAnchor = (p_3: PointSaved) => p_3.p == 0 && (forecast_0.points[0]?.id == p_3.id || last(forecast_0.points)?.id == p_3.id);
            const isOutsideRange = (p_4: PointSaved) => p_4.x < xSelect_0.range[0] || p_4.x > xSelect_0.range[1];
            const toAdjust = forecast_0.points.filter(p_5 => !isEndingAnchor(p_5) && isOutsideRange(p_5) && !draggedPoints.some(dp_0 => dp_0.id == p_5.id));
            if (toAdjust.length) {
              const adjust = solveForV(pAdjust => {
                const adjustedPoints = toAdjust.map(p_6 => ({
                  ...p_6,
                  p: clampP(p_6.p + pAdjust)
                }));
                const newStats = percentileStats(getNewDist(adjustedPoints), xSelect_0.range);
                return {
                  result: newStats.percentiles,
                  adjustedPoints
                };
              }, dragPoints.stats.percentiles, {
                tol: 0.01,
                maxIter: 10,
                initialLearningRate: 0.01,
                startV: delta.y > 0 ? -0.01 : 0.01
              });
              compensatingAdjustments = adjust.adjustedPoints;
            }
          }
          onDelta_0({
            points: sortBy(withAdjustPoints(draggedPoints, compensatingAdjustments), p_7 => p_7.x)
          });
        } else if (startX) {
          const range = sortBy([clampX_0(startX), clampX_0(startX + scale_1.xRatio * delta.x)], n => n) as [number, number];
          const wideEnough = Rnge.width(range) / Rnge.width(boundsTuple_0) > 0.01;
          if (wideEnough) setXSelect_0({
            ...(xSelect_0 ?? {
              locked: false
            }),
            range
          });
        }
      });
    }, [forecast.points]),
    onStart: ({
      x: x_3
    }) => {
      if (!latest.current) return;
      const {
        scale: scale_2,
        forecast: forecast_1,
        dist: dist_0,
        xSelect: xSelect_1,
        mode: mode_1,
        bounds: bounds_0
      } = latest.current;
      const pointFocused = latest.current.hovered ?? (pointClicked.current && Date.now() - pointClicked.current.ts < 200 ? pointClicked.current.id : undefined);
      if (!scale_2) return;
      if (mode_1 == 'drag') {
        dragState.current = {
          startBounds: {
            ...bounds_0
          }
        };
      } else if (pointFocused) {
        dragState.current = {
          dragPoints: {
            points: indexBy(forecast_1.points, p_8 => p_8.id),
            stats: xSelect_1 && Dist.isValid(dist_0) ? percentileStats(dist_0!, xSelect_1.range) : undefined
          }
        };
      } else if (forecast_1.points.length) {
        dragState.current = {
          startX: scale_2.x.invert(x_3)
        };
      }
    },
    onEnd: () => {
      if (!dragState.current || !latest.current) return;
      const {
        xSelect: xSelect_2,
        forecast: forecast_2,
        setUIState: setUIState_0,
        mode: mode_2
      } = latest.current;
      if (mode_2 === 'drag') return; // Don't handle selection in pan mode
      const {
        startX: startX_0
      } = dragState.current;
      if (startX_0 && xSelect_2) {
        setUIState_0({
          ids: forecast_2.points.filter(p_9 => Rnge.between(p_9.x, xSelect_2.range)).map(p_10 => p_10.id),
          delta: {
            selected: true
          },
          others: {
            selected: false
          }
        });
      }
      dragState.current = null;
    },
    sansDragClick: e => {
      const {
        editMode: editMode_0,
        scale: scale_3,
        newPoint,
        mode: mode_3
      } = latest.current;
      if (mode_3 === 'drag') return; // Don't handle clicks in pan mode
      if (editMode_0) {
        if (!scale_3) return;
        if (!e.currentTarget) return;
        var rect = e.currentTarget.getBoundingClientRect();
        const dx_0 = Math.max(0, Math.round(e.pageX - rect.left - (window.pageXOffset ?? window.scrollX))); // borrowed from useMouse
        const dy = Math.max(0, Math.round(e.pageY - rect.top - (window.pageYOffset ?? window.scrollY)));
        const x_4 = scale_3.x.invert(dx_0);
        const p_11 = clampP(scale_3.y.invert(dy));
        newPoint(makePoint({
          x: x_4,
          p: p_11
        }));
      } else {
        if (!e.shiftKey) {
          setUIState({
            others: {
              selected: false
            }
          });
          setXSelect(undefined); // Clear the selected range when clicking empty space
        }
      }
    }
  });
  const xSelectStats = useMemo(() => xSelect?.range && Dist.isValid(dist) ? percentileStats(dist, xSelect.range) : undefined, [xSelect, dist]);
  const buttonLockRef = useNativeEvents<HTMLButtonElement>(makeEvent('click', e_0 => {
    e_0.stopPropagation();
    const {
      xSelect: xSelect_3
    } = latest.current;
    if (xSelect_3) setXSelect({
      ...xSelect_3,
      locked: !xSelect_3.locked
    });
  }, {
    capture: true
  }));
  const buttonXRef = useNativeEvents<HTMLButtonElement>(makeEvent('click', e_1 => {
    e_1.stopPropagation();
    latest.current.setXSelect(undefined);
  }, {
    capture: true
  }));
  const fmtOptions = Fmt.getOptions(question);
  const mergedRefs = useMergedRef(canvasDrag.ref, sizeRef, mouse.ref);
  return <div ref={mergedRefs} style={style} className={twMerge('full relative select-none overflow-hidden', mode === 'drag' && 'cursor-grab active:cursor-grabbing', className)}>
      {scale_0 ? <>
          {dist && Dist.isValid(dist) && <div className='absolute inset-0 overflow-hidden'>
              <DistributionChart margin={m} xScale={scale_0.x} yScale={scale_0.y} chart={{
          bounds: boundsTuple,
          unitLabel: question.unitLabel,
          format: question.numberFormat,
          watermark: showGroup ? 'You' : undefined,
          formats: fmtOptions.absolute
        } as DistributionChartProps['chart']} dist={dist} fadeLabelsOnHover={false} />
            </div>}
          {xSelect && <div id='xSelect' className=' absolute  flex h-full pt-[14px] flex-col' style={{
        left: scale_0.x(xSelect.range[0]),
        top: 0,
        width: scale_0.x(xSelect.range[1]) - scale_0.x(xSelect.range[0])
      }}>
              {xSelectStats && <div className='relative mx-[-1em] flex grow-0 items-center justify-between'>
                  <b className='text-sm text-gray-400'>{fmtNum(round(xSelectStats.startPercentile), '0#o')}</b>
                  <div className='flex items-center'>
                    <div className='mt-1 text-center text-gray-400'>
                      <b className='text-gray-600'>{fmtNum(xSelectStats.percentiles, '#')}</b>{' '}
                      <span className='text-sm'>pts</span>
                    </div>
                    <Tooltip withinPortal label={`(${xSelect.locked ? 'locked' : 'unlocked'}). When locked, points inside this range won't auto-adjust, and this range will be kept at ${fmtNum(xSelectStats.percentiles, '#')} percentile points`}>
                      <div>
                        <ActionIcon ref={buttonLockRef}>
                          {xSelect.locked ? <HiLockClosed /> : <HiLockOpen color={colors.gray[3]} />}
                        </ActionIcon>
                      </div>
                    </Tooltip>
                  </div>
                  <b className='text-sm text-gray-400'>{fmtNum(round(xSelectStats.endPercentile), '0#o')}</b>
                </div>}
              <div className='bg-primary/5 border-primary/20 relative mt-[-4px] grow border-x-2'>
                {xSelectStats && <div className='absolute right-0 top-0'>
                    <ActionIcon ref={buttonXRef}>
                      <HiX />
                    </ActionIcon>
                  </div>}
              </div>
            </div>}
          {editMode && !hovered && mouse.x != 0 && mouse.y != 0 && <Point variant='to-add' {...{
        x: mouse.x,
        y: clamp(mouse.y, {
          max: height - m.bottom
        })
      }} />}
          {forecast.points && forecast.points.length == 0 && <Center className='h-full'>
              <div className='max-w-[30em] text-gray-400'>
                <p className='font-semibold'>no points yet...</p>
                <ul>
                  <li>Enter a logistic, then switch to draw</li>
                  <li>
                    Change to <i>add/remove points mode</i> (<KbdComboLabel combo={{
                key: 'Meta'
              }} />) and click to add a
                    point
                  </li>
                </ul>
              </div>
            </Center>}
          {forecast.points?.map(p_12 => {
        const {
          id: id_0
        } = p_12;
        const {
          selected: selected_0
        } = ptUIState[id_0] ?? {};
        return <Point key={id_0} interactive={{
          id: id_0,
          editMode,
          uPoints,
          setPointClicked: id_1 => {
            pointClicked.current = {
              id: id_1,
              ts: Date.now()
            };
          }
        }} variant={hovered == id_0 ? editMode ? 'to-delete' : 'to-select' : selected_0 ? 'selected' : 'normal'} x={scale_0.x(p_12.x)} y={scale_0.y(p_12.p)} onMouseEnter={() => {
          setHovered(p_12.id);
        }} onMouseLeave={() => {
          setHovered(undefined);
        }} />;
      })}
          {tails.map(t => <ForecastAmountTailEdit key={t.side} {...t} />)}
          {tailsHard.map(t_0 => <ForecastAmountTailEdit key={t_0.side} {...t_0} xScale={scale_0.x} debugName='hard' />)}
        </> : <></>}
    </div>;
};
const ptSize = 18;
const Point = ({
  variant,
  x,
  y,
  id,
  style,
  interactive,
  ...props
}: {
  variant?: 'to-select' | 'selected' | 'to-add' | 'to-delete' | 'normal';
  x: number;
  y: number;
  interactive?: {
    id: string;
    uPoints: UsePoints;
    editMode: boolean;
    setPointClicked: (id: string) => void;
  };
} & ComponentProps<'div'>) => {
  const latest = useLatest(interactive ? {
    ...interactive,
    selected: interactive.uPoints.selectedIds.includes(interactive.id)
  } : undefined);
  const clickNoDragRef = useClickSansDrag((e: Pick<MouseEvent, 'shiftKey' | 'stopPropagation'>) => {
    if (!latest.current) return;
    const {
      id: id_0,
      uPoints,
      editMode
    } = latest.current;
    e.stopPropagation();
    if (editMode) {
      uPoints.deletePoints([id_0]);
    } else if (!e.shiftKey) {
      uPoints.setUIState({
        id: id_0,
        delta: {
          selected: true
        },
        others: {
          selected: false
        }
      });
    }
  });
  const onMouseDown = useCallback((e_0: Pick<React.MouseEvent, 'shiftKey' | 'stopPropagation'>) => {
    // select a point in preparation for dragging, the other behaviors happen onClick
    if (!latest.current) return;
    const {
      id: id_1,
      uPoints: uPoints_0,
      editMode: editMode_0,
      setPointClicked,
      selected
    } = latest.current;
    if (editMode_0) return;
    setPointClicked(id_1);
    if (e_0.shiftKey) uPoints_0.setUIState({
      id: id_1,
      delta: {
        selected: !selected
      }
    });else if (!selected) uPoints_0.setUIState({
      id: id_1,
      delta: {
        selected: true
      },
      others: {
        selected: false
      }
    });
  }, []);
  return <div ref={clickNoDragRef} {...props} className={`absolute cursor-context-menu rounded-full bg-gray-500 outline-2
 outline-white data-[variant=selected]:bg-primary/80 data-[variant=to-delete]:bg-red-500 data-[variant=to-select]:bg-bg-primary data-[variant=to-add]:opacity-50
data-[variant=to-select]:opacity-50 
`} data-variant={variant} onMouseDown={onMouseDown} onTouchStart={onMouseDown} style={{
    width: ptSize,
    height: ptSize,
    outlineStyle: 'solid',
    left: x,
    top: y,
    transform: `translate(-50%, -50%)`,
    ...style
  }}>
      {/* <div className='absolute border-2 border-red-500 top-[100%]'>{interactive?.id}</div> */}
    </div>;
};
type EventHandlerProps<T extends keyof HTMLElementEventMap> = {
  type: T;
  listener: (event: HTMLElementEventMap[T]) => void;
  options?: boolean | AddEventListenerOptions;
};
const makeEvent = <T extends keyof HTMLElementEventMap,>(type: T, listener: EventHandlerProps<T>['listener'], options?: EventHandlerProps<T>['options']): AnyHandlerProps => ({
  type,
  listener: listener as EventListenerOrEventListenerObject,
  options
});
type AnyHandlerProps = {
  type: string;
  listener: EventListenerOrEventListenerObject;
  options?: boolean | AddEventListenerOptions;
};

/** easy way to add native event listeners. This doesn't update with new listeners, so use ref's if you need to */
const useNativeEvents = <T extends HTMLElement = HTMLDivElement,>(...listeners: AnyHandlerProps[]) => {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      listeners.forEach(h => el.addEventListener(h.type, h.listener, h.options));
    }
    return () => {
      if (el) listeners.forEach(h_0 => el.removeEventListener(h_0.type, h_0.listener));
    };
  }, [ref.current]);
  return ref;
};
function _temp(p) {
  return p.x;
}
function _temp2(p_2) {
  return p_2.id;
}